import { useMemo } from 'react';
import { getPECSectionColumns } from '../../../../utils/data';
import { PECScanData } from '../../../../utils/models';
import { getChunkedData, getNumberOfMeasurementsForPECScan, getPageRange } from '../../../../utils/utilities';

import Conditional from '../../../common/conditional';
import CustomPecScanTable from '../../../custom-reports/custom-pec/custom-pec-scan-table';
import CustomSection from '../../../custom-reports/custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import usePecScanOverview from '../../../../hooks/usePecScanOverview';
import classNames from '../pdf.module.scss';

interface PECScanSummaryProps {
	pecScans: PECScanData[];
	remark: string;
	material: string;
}

const PECScanSummary = (props: PECScanSummaryProps) => {
	const calculatePecScanSummary = usePecScanOverview();

	const pecScanSummary = props.pecScans.flatMap((scanData) => calculatePecScanSummary(scanData).flat()).map((scanData, index) => ({ ...scanData, srNo: index + 1}))
	const totalNumberOfMeasurements = props.pecScans.reduce((a, b) => a + getNumberOfMeasurementsForPECScan(b), 0);

	const pecScanWithSrNo = useMemo(() => {
		return props.pecScans.map((scan, index) => ({ ...scan, srNo: index + 1 }));
	}, [props.pecScans]);
	
	const chunkCount = useMemo(() => {
		const scansWithLongResult = pecScanSummary.filter((summary) => summary.result.length > 20);
		if (scansWithLongResult.length > 3) {
			return 7;
		};

		return 8;
	}, [pecScanSummary]);

	const pecChunks = getChunkedData(pecScanWithSrNo, chunkCount);
	const summaryChunks = getChunkedData(pecScanSummary, chunkCount)

	return (
			<>
				{getPageRange(pecScanSummary.length / chunkCount).map((page, index) => (
					<CustomSection sectionTitle="Summary" key={page} sectionSubtitle="Measurement Details">
							<div className={classNames.pecAnomaly} key={page}>
								<div className="p-2">
									<div className="table-responsive">
										<PDFTablePage data={pecChunks[index] ?? []} columns={getPECSectionColumns()} />
									</div>


									<Conditional isVisible={index === pecChunks.length - 1}>
										<div className="container-fluid overflow-hidden">
											<div className="row">
												<div className="col p-0">
													<div className={classNames.pecAnomalyTotal}>Total Number of Measurements: <span className="ms-3">{totalNumberOfMeasurements}</span></div>
												</div>
												<div className="col p-0">
													<div className={classNames.pecAnomalyTotal}>Material: <span className="ms-3">{props.material}</span></div>
												</div>
											</div>
										</div>
									</Conditional>
								</div>

								<div className="p-2">
									<div className={classNames.pecAnomalyTitle}>Scan Summary Details</div>

									<div className="table-responsive">
										<CustomPecScanTable data={summaryChunks[index] ?? []} />
									</div>
								</div>

								<Conditional isVisible={index === summaryChunks.length - 1}>
									<div className="p-2">
										<div className={classNames.pecAnomalyConclusion}>
											<div className={classNames.pecAnomalyConclusionHeader}>Remark, if any</div>
											<div className={classNames.pecAnomalyConclusionBody}>{props.remark}</div>
										</div>
									</div>
								</Conditional>
							</div>
					</CustomSection>
				))}
			</>
	);
};

export default PECScanSummary;
